import { useDrag, useDrop } from "react-dnd";
import { IoMdRemoveCircle } from "react-icons/io";
import { MdOutlineDragIndicator } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import {
  removePodcastFromPlaylist,
  removePodcastFromPlaylistLoading,
  removeSongFromPlaylist,
  removeSongFromPlaylistLoading,
  setCurrentAudio,
  setCurrentPodcast,
} from "redux/AudioPlayerRedux";
import { formatDuration } from "utils/timeFunctions";
import { Tooltip as ReactTooltip } from "react-tooltip";

const ItemType = "ITEM";

function DraggablePlaylistCard({
  song,
  currentSong,
  index,
  moveItem,
  music,
  radio,
  podcast,
}: any) {
  const dispatch = useDispatch<any>();

  const { isPlaying } = useSelector((state: any) => state?.audio);
  const isActive = currentSong?.id === song?.id;
  const isThisPlaying = isActive && isPlaying;

  // draggable options
  const [, ref] = useDrag({
    type: ItemType,
    item: { index },
  });

  const [, drop] = useDrop({
    accept: ItemType,
    hover: (draggedItem: { index: number; song: any; currentSong: any }) => {
      if (draggedItem.index !== index) {
        moveItem(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  const chooseSong = () => {
    if (music) {
      dispatch(setCurrentAudio(song));
    } else if (podcast) {
      dispatch(setCurrentPodcast(song));
    }
  };

  const handleDelete = async () => {
    if (music) {
      const obj = { songId: song.id, index: index };
      await dispatch(removeSongFromPlaylistLoading());
      await dispatch(removeSongFromPlaylist(obj));
    } else if (podcast) {
      const obj = { songId: song.id, index: index };
      await dispatch(removePodcastFromPlaylistLoading());
      await dispatch(removePodcastFromPlaylist(obj));
    }
  };

  const artwork = music
    ? song?.artwork_url
    : podcast
      ? song?.podcast?.artwork_url
      : "";

  const artistName = music
    ? song?.artists?.[0]?.name
    : podcast
      ? song?.artist?.name
      : "";

  return (
    <>
      {song && (
        <div
          ref={(node) => ref(drop(node))}
          className={`group mb-1 flex items-center gap-3 rounded-xl px-2 py-2 transition-colors duration-200 ${
            isActive ? "bg-white/[0.07]" : "hover:bg-white/[0.05]"
          }`}
        >
          {/* drag handle */}
          <MdOutlineDragIndicator className="shrink-0 cursor-grab text-xl text-[#7B9DB4] active:cursor-grabbing" />

          {/* thumbnail (click to play) */}
          <div
            className="relative h-14 w-14 shrink-0 cursor-pointer overflow-hidden rounded-lg bg-white/5"
            onClick={chooseSong}
          >
            {artwork && (
              <img
                src={artwork}
                alt={song?.title || "artwork"}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover"
              />
            )}

            {/* now-playing wave */}
            {isThisPlaying && (
              <div className="absolute inset-0 flex items-end justify-center gap-[3px] bg-black/45 pb-3">
                <span className="eq-bar" style={{ animationDelay: "0s" }} />
                <span className="eq-bar" style={{ animationDelay: "0.2s" }} />
                <span className="eq-bar" style={{ animationDelay: "0.4s" }} />
              </div>
            )}
          </div>

          {/* title + artist (click to play) */}
          <div
            className="flex min-w-0 flex-1 cursor-pointer flex-col"
            onClick={chooseSong}
          >
            <p
              className={`truncate text-[14px] font-semibold ${
                isActive ? "text-red-500" : "text-white"
              }`}
              data-tooltip-id={`queue-song-${song.id}`}
            >
              {song?.title}
            </p>
            {song?.title?.length > 15 && (
              <ReactTooltip
                id={`queue-song-${song.id}`}
                place="top"
                content={song.title}
              />
            )}
            <p className="truncate mt-1 text-[12px] text-[#7B9DB4]">
              {artistName}
            </p>
          </div>

          {/* duration */}
          <span className="shrink-0 text-[12px] tabular-nums text-[#7B9DB4]">
            {song?.duration ? formatDuration(song?.duration) : "00:00"}
          </span>

          {/* remove */}
          <button
            className="shrink-0 text-xl text-white/40 opacity-0 transition-colors duration-200 hover:text-red-500 group-hover:opacity-100"
            onClick={handleDelete}
            aria-label="Remove from queue"
          >
            <IoMdRemoveCircle />
          </button>

          {/* scoped styles for the now-playing wave */}
          <style>{`
        .eq-bar {
          width: 3px;
          height: 50%;
          border-radius: 2px;
          background: #ef4444;
          animation: eqPulse 0.9s ease-in-out infinite;
        }
        @keyframes eqPulse {
          0%, 100% { transform: scaleY(0.3); }
          50% { transform: scaleY(1); }
        }
      `}</style>
        </div>
      )}
    </>
  );
}

export default DraggablePlaylistCard;
